import React from 'react'
import {CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis, Legend} from 'recharts'
import {Rental} from '../../../common/types/rental'
import {getDashboardLastNDaysEndingYesterdayKeys} from '../../../common/utils/dashboardDateKeys'
import {generateRentalsPerDayData, RentalsPerDayPoint} from './utils/generateRentalsPerDayData'
import {formatDate, parseISO, subDays} from '../../../common/utils/dateUtils'

interface Props {
  rentals: Rental[]
  /** window size in days for the smoothing line (default 14) */
  rollingAverage?: number
  /** multiplier converting users → pifflers (default 4) */
  factor?: number
  /** optional filter range (YYYY-MM-DD or ISO); honors page filter when provided */
  fromDate?: string
  endDate?: string
}

type PifflersPoint = {
  date: string
  numberOfPifflers: number
  rollingPifflers: number
}

function toLocalDayKey(value?: string): string | undefined {
  if (!value) return undefined
  // If already YYYY-MM-DD, keep it
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  // Else parse ISO-ish and format to local YYYY-MM-DD
  const d = parseISO(value)
  return isNaN(d.getTime()) ? undefined : formatDate(d)
}

/**
 * Shows estimated pifflers/day.
 * If fromDate/endDate are provided, the x-axis uses that range (clamped to yesterday).
 * Otherwise falls back to last 14 days ending yesterday.
 */
export const PifflersPerDay: React.FC<Props> = ({rentals, rollingAverage = 14, factor = 4, fromDate, endDate}) => {
  // Normalize incoming filter dates (local YYYY-MM-DD)
  const reqStart = toLocalDayKey(fromDate)
  const reqEndRaw = toLocalDayKey(endDate)

  // Clamp end to "yesterday" (local day key)
  const yesterdayKey = getDashboardLastNDaysEndingYesterdayKeys(1).endKey
  const effectiveEnd = reqEndRaw && reqEndRaw <= yesterdayKey ? reqEndRaw : yesterdayKey

  // If start provided, use it; else back off 13 days from effectiveEnd for a 14-day window
  const effectiveStart = reqStart ? reqStart : formatDate(subDays(parseISO(effectiveEnd), 13))

  // Ensure start is not after end; if it is, snap start = end
  const startKey = effectiveStart > effectiveEnd ? effectiveEnd : effectiveStart
  const endKey = effectiveEnd

  // Base series (rentals/day) from shared generator
  const base = generateRentalsPerDayData(rentals, startKey, endKey, rollingAverage)

  // Scale to pifflers and compute our own rolling average to guarantee presence
  const pifflersRaw = (base as RentalsPerDayPoint[]).map((d) => ({
    date: d.date,
    numberOfPifflers: d.numberOfRentals * factor,
  }))

  const data: PifflersPoint[] = pifflersRaw.map((_, i) => {
    const start = Math.max(0, i - (rollingAverage - 1))
    const window = pifflersRaw.slice(start, i + 1)
    const sum = window.reduce((acc, pt) => acc + (pt.numberOfPifflers ?? 0), 0)
    const avg = window.length ? sum / window.length : 0
    return {
      date: pifflersRaw[i].date,
      numberOfPifflers: pifflersRaw[i].numberOfPifflers,
      rollingPifflers: avg,
    }
  })

  const RAW_LABEL = 'Pifflare/dag'
  const AVG_LABEL = `Rullande ${rollingAverage} d snitt`

  return (
    <LineChart width={800} height={300} data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="date" />
      <YAxis allowDecimals={false} />
      <Legend />
      <Tooltip
        itemSorter={(item: any) => {
          const name = (item?.name || item?.dataKey) as string
          const order: Record<string, number> = {
            [RAW_LABEL]: 0,
            [AVG_LABEL]: 1,
          }
          return order[name] ?? 99
        }}
        formatter={(value: number, name: string) => [Math.round(value as number), name]}
        labelFormatter={(label: string) => label}
      />
      <Line type="monotone" dataKey="numberOfPifflers" name={RAW_LABEL} stroke="#044A5A" dot />
      <Line type="monotone" dataKey="rollingPifflers" name={AVG_LABEL} stroke="#FF9900" dot={false} />
    </LineChart>
  )
}
